import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Mail } from 'lucide-react'
import { useSuspenseQuery } from '@tanstack/react-query'

import { PageContainer } from '@/components/common/page-container'
import { PageHeader } from '@/components/common/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { Pagination } from '@/components/ui/pagination'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { CoverLetterPanel } from '@/features/ai/cover-letter-panel'
import { matchesListQuery, type Match } from '@/api/matches'
import { RouteError } from '@/components/common/route-error'

/**
 * /cover-letters — pick a match on the left, generate on the right.
 *
 * Post P0.3: switched from useAllMatches (which paginated through every
 * match) to a paginated matchesListQuery. The sidebar is a single page
 * with a paginator at the bottom; users with hundreds of matches can
 * page through to find the one they want, rather than waiting for a
 * 10-request fan-out to settle before any selection works.
 *
 * Page size 100 mirrors the matches table — keeps the cache hot when
 * the user bounces between the two screens.
 */

const PER_PAGE = 100

export const Route = createFileRoute('/_authed/cover-letters')({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(matchesListQuery(1, PER_PAGE)),
  component: CoverLettersPage,
  errorComponent: RouteError,
})

function CoverLettersPage() {
  const [page, setPage] = useState(1)
  const { data } = useSuspenseQuery(matchesListQuery(page, PER_PAGE))
  const [selected, setSelected] = useState<Match | undefined>(undefined)

  // Auto-select the first match on the current page when nothing's picked.
  // Resets when the user pages (selected becomes undefined again via the
  // onPageChange handler below).
  useEffect(() => {
    if (!selected && data.items[0]) {
      setSelected(data.items[0])
    }
  }, [data.items, selected])

  return (
    <PageContainer>
      <PageHeader
        title="Cover Letters"
        description="Generate a tailored letter per match. Tone, length, and emphasis are yours to set."
      />

      {data.items.length === 0 ? (
        <EmptyState
          icon={Mail}
          title="No matches yet"
          description="Create a loop first; we'll surface matches you can write to."
        />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          <aside className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto">
            <div className="flex items-center justify-between px-1 pb-1 text-xs text-muted">
              <span>
                {data.items.length} of {data.total}
              </span>
            </div>
            {data.items.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelected(m)}
                className={`rounded-[var(--radius-card)] border p-3 text-left transition-colors ${
                  selected?.id === m.id
                    ? 'border-primary bg-info-bg/40'
                    : 'border-divider bg-surface-base hover:bg-surface-sunken'
                }`}
              >
                <p className="truncate text-sm font-semibold text-ink">{m.jobTitle}</p>
                <p className="truncate text-xs text-muted">{m.company || 'Unknown'}</p>
              </button>
            ))}
            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={data.total}
              onPageChange={(p) => {
                setPage(p)
                setSelected(undefined)
              }}
              className="mt-2"
            />
          </aside>

          <Card>
            <CardHeader>
              <CardTitle>{selected?.jobTitle ?? 'Pick a match'}</CardTitle>
              {selected ? (
                <p className="text-xs text-muted">{selected.company || 'Unknown company'}</p>
              ) : null}
            </CardHeader>
            <div className="p-5 pt-0">
              {selected ? (
                <CoverLetterPanel matchId={selected.id} />
              ) : (
                <p className="text-sm text-muted">Select a match on the left.</p>
              )}
            </div>
          </Card>
        </div>
      )}
    </PageContainer>
  )
}
